import { config } from './config';

export interface Deal {
  dealId: string;
  tenantId: string;
  name: string;
  status: string;
  createdAt: string;
}

export interface Job {
  jobId: string;
  jobType: string;
  dealId: string;
  status: string;
  inputRefs?: string;
  outputRefs?: string;
  blockedOn?: string;
  errorDetail?: string;
  costCeilingUsd?: number;
  costUsedUsd?: number;
  startedAt?: string;
  completedAt?: string;
}

export interface Artifact {
  entityType: string;
  dealId: string;
  status: string;
  createdAt: string;
  payload: string;
}

export interface PipelineExecution {
  cimDocumentId: string;
  s3Key: string;
  executionArn: string;
}

export interface AuditEvent {
  auditEventId: string;
  tenantId: string;
  dealId?: string;
  userId?: string;
  action: string;
  targetEntity: string;
  targetId: string;
  before?: string;
  after?: string;
  occurredAt: string;
  source?: string;
  reason?: string;
}

export interface MandateConfig {
  tenantId: string;
  version: number;
  status: string;
  sectors: string[];
  geography: string[];
  minEbitda?: number;
  maxEbitda?: number;
  minEnterpriseValue?: number;
  maxEnterpriseValue?: number;
  minIrr?: number;
  minMoic?: number;
  minDscr?: number;
  minFccr?: number;
  thesisBreakers: string[];
  houseVoice?: string;
  updatedAt: string;
}

async function graph<T>(idToken: string, query: string, variables?: Record<string, unknown>): Promise<T> {
  const res = await fetch(config.graphqlUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: idToken,
    },
    body: JSON.stringify({ query, variables }),
  });
  const body = (await res.json()) as { data?: T; errors?: { message: string }[] };
  if (!res.ok || body.errors?.length) {
    throw new Error(body.errors?.map((err) => err.message).join('; ') || `GraphQL failed: ${res.status}`);
  }
  return body.data as T;
}

export async function createDeal(idToken: string, name: string): Promise<Deal> {
  const data = await graph<{ createDeal: Deal }>(
    idToken,
    `mutation CreateDeal($input: CreateDealInput!) {
      createDeal(input: $input) { dealId tenantId name status createdAt }
    }`,
    { input: { name } },
  );
  return data.createDeal;
}

export async function getMandateConfig(idToken: string): Promise<MandateConfig | null> {
  const data = await graph<{ mandateConfig: MandateConfig | null }>(
    idToken,
    `query Mandate {
      mandateConfig {
        tenantId version status sectors geography minEbitda maxEbitda minEnterpriseValue maxEnterpriseValue
        minIrr minMoic minDscr minFccr thesisBreakers houseVoice updatedAt
      }
    }`,
  );
  return data.mandateConfig;
}

export async function upsertMandateConfig(idToken: string, input: Partial<MandateConfig>): Promise<MandateConfig> {
  const data = await graph<{ upsertMandateConfig: MandateConfig }>(
    idToken,
    `mutation UpsertMandate($input: MandateConfigInput!) {
      upsertMandateConfig(input: $input) {
        tenantId version status sectors geography minEbitda maxEbitda minEnterpriseValue maxEnterpriseValue
        minIrr minMoic minDscr minFccr thesisBreakers houseVoice updatedAt
      }
    }`,
    { input },
  );
  return data.upsertMandateConfig;
}

export async function listDeals(idToken: string): Promise<Deal[]> {
  const data = await graph<{ deals: Deal[] }>(
    idToken,
    `query Deals { deals { dealId tenantId name status createdAt } }`,
  );
  return data.deals;
}

export async function requestCimUpload(
  idToken: string,
  dealId: string,
  filename: string,
  contentType = 'application/pdf',
  fileSizeBytes: number,
): Promise<{ cimDocumentId: string; uploadUrl: string; uploadFields?: string; uploadMethod: string; s3Key: string; executionArn?: string }> {
  const data = await graph<{
    requestCimUpload: { cimDocumentId: string; uploadUrl: string; uploadFields?: string; uploadMethod: string; s3Key: string; executionArn?: string };
  }>(
    idToken,
    `mutation Upload($dealId: ID!, $filename: String, $contentType: String, $fileSizeBytes: Float, $autoStartPipeline: Boolean) {
      requestCimUpload(dealId: $dealId, filename: $filename, contentType: $contentType, fileSizeBytes: $fileSizeBytes, autoStartPipeline: $autoStartPipeline) {
        cimDocumentId
        uploadUrl
        uploadFields
        uploadMethod
        s3Key
        executionArn
      }
    }`,
    { dealId, filename, contentType, fileSizeBytes, autoStartPipeline: false },
  );
  return data.requestCimUpload;
}

export async function rerunPipeline(idToken: string, dealId: string, reason?: string): Promise<PipelineExecution> {
  const data = await graph<{ rerunPipeline: PipelineExecution }>(
    idToken,
    `mutation RerunPipeline($dealId: ID!, $reason: String) {
      rerunPipeline(dealId: $dealId, reason: $reason) {
        cimDocumentId
        s3Key
        executionArn
      }
    }`,
    { dealId, reason },
  );
  return data.rerunPipeline;
}

export async function listJobs(idToken: string, dealId: string): Promise<Job[]> {
  const data = await graph<{ dealJobs: Job[] }>(
    idToken,
    `query Jobs($dealId: ID!) {
      dealJobs(dealId: $dealId) {
        jobId jobType dealId status inputRefs outputRefs blockedOn errorDetail costCeilingUsd costUsedUsd startedAt completedAt
      }
    }`,
    { dealId },
  );
  return data.dealJobs;
}

export async function approveJobCostOverrun(
  idToken: string,
  dealId: string,
  jobId: string,
  newCostCeilingUsd: number,
  reason: string,
): Promise<Job> {
  const data = await graph<{ approveJobCostOverrun: Job }>(
    idToken,
    `mutation ApproveJobCost($dealId: ID!, $jobId: ID!, $newCostCeilingUsd: Float!, $reason: String!) {
      approveJobCostOverrun(dealId: $dealId, jobId: $jobId, newCostCeilingUsd: $newCostCeilingUsd, reason: $reason) {
        jobId jobType dealId status inputRefs outputRefs blockedOn errorDetail costCeilingUsd costUsedUsd startedAt completedAt
      }
    }`,
    { dealId, jobId, newCostCeilingUsd, reason },
  );
  return data.approveJobCostOverrun;
}

export async function abortJob(idToken: string, dealId: string, jobId: string, reason: string): Promise<Job> {
  const data = await graph<{ abortJob: Job }>(
    idToken,
    `mutation AbortJob($dealId: ID!, $jobId: ID!, $reason: String!) {
      abortJob(dealId: $dealId, jobId: $jobId, reason: $reason) {
        jobId jobType dealId status inputRefs outputRefs blockedOn errorDetail costCeilingUsd costUsedUsd startedAt completedAt
      }
    }`,
    { dealId, jobId, reason },
  );
  return data.abortJob;
}

export async function latestArtifact(idToken: string, dealId: string, entityType: string): Promise<Artifact | null> {
  const data = await graph<{ latestArtifact: Artifact | null }>(
    idToken,
    `query LatestArtifact($dealId: ID!, $entityType: String!) {
      latestArtifact(dealId: $dealId, entityType: $entityType) { entityType dealId status createdAt payload }
    }`,
    { dealId, entityType },
  );
  return data.latestArtifact;
}

export async function listArtifacts(idToken: string, dealId: string, entityType?: string): Promise<Artifact[]> {
  const data = await graph<{ dealArtifacts: Artifact[] }>(
    idToken,
    `query DealArtifacts($dealId: ID!, $entityType: String) {
      dealArtifacts(dealId: $dealId, entityType: $entityType) { entityType dealId status createdAt payload }
    }`,
    { dealId, entityType },
  );
  return data.dealArtifacts;
}

export async function listAuditEvents(idToken: string, dealId?: string): Promise<AuditEvent[]> {
  const data = await graph<{ auditEvents: AuditEvent[] }>(
    idToken,
    `query AuditEvents($dealId: ID) {
      auditEvents(dealId: $dealId) {
        auditEventId tenantId dealId userId action targetEntity targetId before after occurredAt source reason
      }
    }`,
    { dealId },
  );
  return data.auditEvents;
}

export async function approveBidMemo(idToken: string, dealId: string, bidMemoVersionId: string): Promise<Artifact> {
  const data = await graph<{ approveBidMemo: Artifact }>(
    idToken,
    `mutation ApproveBidMemo($dealId: ID!, $bidMemoVersionId: ID!) {
      approveBidMemo(dealId: $dealId, bidMemoVersionId: $bidMemoVersionId) {
        entityType dealId status createdAt payload
      }
    }`,
    { dealId, bidMemoVersionId },
  );
  return data.approveBidMemo;
}
